<template>
    <div class="lock-plan-wrapper">
        <lock-plan-subscribe-section
            v-if="pageDataLoaded"
            @ready="sectionReady = true"
            @subscribe="selectPlanProduct"
            @goCheckout="goCheckout"
            :productsArgs="productsArgs"
            :sub="sub"
            :device-identity="deviceIdentity"
            :displayOnly="displayOnly"
        />
        <div v-if="!ready" class="loading-overlay">
            <ReoProcessing />
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { IDeviceIdentity, ISubscribeArgsWithDevice } from '@/components/PlanSubscribeSection/lib/types';
import { getQueryParam, unitTransfer } from '@/common/js/utils';
import { config } from 'CONFIG';
import reolinkGa from '@/common/js/gtagPageview';
import { ESubscribeMode } from '@/interface';
import { ISubscription } from '@reolink-cloud/interface';
import LockPlanSubscribeSection from 'VIEW/user/Subscribe/LockPlan/components/LockPlanSubscribeSection.vue';
import { mapGetters } from 'vuex';
import { getIccidByUid,  getSubscriptionById } from '@/api/subscriptions';
import { uidHandler } from '@/lib';
import { getPlanById } from '@/api/cart';
import ReoProcessing from '@/basic/processing.vue';
import { redirectIfSimHasSubscription } from './lockPlanSubscription';

export default Vue.extend({
    name: 'LockPlanSelectSection',
    components: {
        LockPlanSubscribeSection,
        ReoProcessing,
    },
    data () {

        return {
            planList: [] as any[],
            email: this.$store.state.user.user.email.address,
            config,
            planId: ( this.$route.query.p_id ?? '0' ) as string,
            betaStock: 0,
            lifeTime: 0,
            activeItem: 100,
            disabledPlan: [] as any[],
            showSubDialog: false,
            planVideoQuantity: 0,
            currentVideoQuantity: 0,
            planTitle: '',
            gtagData: {} as Record<string, any>,
            prevSubId: this.$route.query.ps_id as string | null,
            sub: null as ISubscription | null,
            isIccid: true,
            deviceIdentity: { uid: '', iccid: '' } as IDeviceIdentity,
            pageDataLoaded: false,
            sectionReady: false,
        };

    },
    computed: {
        ...mapGetters( ['country'] as const ),
        displayOnly(): boolean {

            return !!( this.$route.meta as Record<string, any> ).displayOnly;

        },
        verified(): boolean {

            return this.$store.state.user.user.email.status === 'verified';

        },
        productsArgs(): Record<string, string> {

            return {

                legacy: '',
                page: this.prevSubId ? 'update-plan' : 'subscribe'

            };

        },
        ready(): boolean {

            return this.pageDataLoaded && this.sectionReady;

        },

    },
    async created() {

        if ( this.displayOnly ) {

            this.pageDataLoaded = true;
            return;

        }

        if ( this.prevSubId ) {

            const sub = await getSubscriptionById( this.prevSubId );
            const planDetails = await getPlanById( sub.plan );
            planDetails.plan.title = planDetails.series.i18n.name;
            planDetails.plan.series = planDetails.series;
            this.$set( sub, 'plan', planDetails.plan );
            this.sub = sub;

        }
        await this.resolveUidIccid();
        if ( !this.prevSubId ) {

            const hasSubscription = await this.hasSubscription();
            if ( hasSubscription ) {

                return;

            }

        }

        this.pageDataLoaded = true;

    },
    methods: {

        async resolveUidIccid() {

            const encrypted = getQueryParam( this.$route, 'ymh' );
            const decrypted = encrypted.map(( e ) => uidHandler.decrypt( e ));
            const first = decrypted[0];
            this.isIccid = first.length === 20;

            const esimProfile = await getIccidByUid();

            if ( this.isIccid ) {

                const uid = esimProfile.find( item => item.iccid === first )?.uid;

                this.deviceIdentity = { uid: uid ?? '', iccid: first };

            } else {

                const iccid = esimProfile.find( item => item.uid === first )?.iccid;

                this.deviceIdentity = { uid: first, iccid: iccid ?? '' };

            }

        },

        async hasSubscription(): Promise<boolean> {

            return redirectIfSimHasSubscription( this.deviceIdentity.iccid, location => this.$router.replace( location ));

        },

        async goCheckout ( args: {
            p_id: string;
            isCurrentSubscriptionPlan: boolean;
            apply_promotion_usage_limit?: string;
        }): Promise<void> {

            if ( this.displayOnly ) {

                await this.$router.push({
                    path: '/user/my-devices/',
                    query: { tab: 'sim' }
                });
                return;

            }

            reolinkGa.sendECommerceEvent( 'select_item', this.gtagData );
            const isCreate = !this.prevSubId;

            const baseQuery: Record<string, string | string[] | undefined> = {
                ...this.$route.query,
                mode: ( args.isCurrentSubscriptionPlan ? ESubscribeMode.RENEW : this.$route.query.mode ) as string,
                subNew: isCreate ? '1' : '0',
                p_id: args.p_id,
                country: this.country
            };
            if ( args.apply_promotion_usage_limit === '1' ) {

                baseQuery.apply_promotion_usage_limit = '1';

            } else {

                delete baseQuery.apply_promotion_usage_limit;

            }

            if ( this.isIccid ) {

                await this.$router.push({

                    path: '/checkout/',
                    query: baseQuery as Record<string, string | string[]>

                });

            } else {

                await this.$router.push({

                    path: '/checkout/',
                    query: {
                        ...baseQuery,
                        ymh: uidHandler.encrypt( this.deviceIdentity.iccid ?? '' )
                    } as Record<string, string | string[]>

                });

            }

        },
        selectPlanProduct ( args: ISubscribeArgsWithDevice ): Promise<void> {

            const { plan, series } = args;
            this.planId = plan.id;
            this.planVideoQuantity = plan.style['cloud_storage.max_device_quantity'] ?? 0;
            this.planTitle = series.i18n.name;
            this.lifeTime = plan.style['cloud_storage.video_lifetime'] ?? 0;

            let itemListName = `(cloud-Plan)storageRegion:${series.storageRegion};deviceGroup:${series.deviceGroup}`;
            let name = this.planTitle;
            name += `(${plan.duration.amount}-${plan.duration.unit})`;
            name += `(${plan.style['cloud_storage.video_lifetime'] ?? 0}-days,${plan.style['cloud_storage.max_device_quantity'] ?? 0}-cams,${unitTransfer( plan.style['cloud_storage.space_capacity'] ?? 0 )}${unitTransfer( plan.style['cloud_storage.space_capacity'] ?? 0, 'unit' )})`;

            let eventData = {
                items: [{
                    item_id: plan.id,
                    item_name: `(cloud-Plan)${name}`,
                    item_category: 'cloud-Plan',
                    price: Number( plan.pricing.salePrice ),
                    item_list_name: itemListName,
                    item_variant: `mode:create;currency:${plan.pricing.currency}`
                }]
            };
            this.gtagData = eventData;

        },
    },
});
</script>

<style scoped lang="less">
.lock-plan-wrapper {
    position: relative;
    min-height: 100vh;
}

.loading-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: @white;
    z-index: 10;
}
</style>
